<script setup lang="ts">
import { computed } from "vue";
import { Timestamp } from "firebase/firestore";
import type { ICase } from "~~/types/case";

const props = defineProps<{
  open: boolean;
  case: ICase | null;
}>();

const emit = defineEmits<{
  viewCase: [id: string];
  dismiss: [];
}>();

const router = useRouter();

const handleViewCase = () => {
  if (props.case) {
    emit("viewCase", props.case.id);
    router.push(`/case/${props.case.regNum}`);
  }
};

const handleViewCaseWithoutAccept = () => {
  if (props.case) {
    window.open(`/case/${props.case.regNum}`, "_blank");
  }
};

const handleDismiss = () => {
  emit("dismiss");
};

const customerName = computed(() => props.case?.userData?.customer?.name || "Unknown");
const vehicleInfo = computed(() => {
  const vehicle = props.case?.userData?.customer?.vehicle;
  if (!vehicle) return "No vehicle info";
  return `${vehicle.brand || ""} ${vehicle.model || ""} (${vehicle.regNum || "N/A"})`.trim();
});

const providerName = computed(() => props.case?.userData?.provider?.displayName || "Unknown Provider");

const location = computed(() => {
  const from = props.case?.location?.from;
  if (!from) return "Unknown location";
  return from.address || from.generatedAddress || "No address available";
});

const etaDisplay = computed(() => {
  const eta = props.case?.statusTimestamp?.estimatedArrivalAt;
  if (!eta) return "No ETA set";

  const etaDate = eta instanceof Timestamp ? eta.toDate() : new Date(eta);
  const now = new Date();
  const diffMs = etaDate.getTime() - now.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  // Format time
  const timeStr = etaDate.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  if (diffMinutes < 0) {
    const minutesAgo = Math.abs(diffMinutes);
    return `${timeStr} (${minutesAgo} minute${minutesAgo !== 1 ? "s" : ""} ago)`;
  } else if (diffMinutes <= 5) {
    return `${timeStr} (in ${diffMinutes} minute${diffMinutes !== 1 ? "s" : ""})`;
  } else {
    return timeStr;
  }
});

const caseStatus = computed(() => {
  if (!props.case) return "Unknown";
  const status = props.case.status;
  return status.charAt(0).toUpperCase() + status.slice(1);
});

const statusDisplayData = computed(() => {
  return getCaseStatusIconColor(props.case?.status);
});
</script>

<template>
  <Dialog :open="open">
    <DialogContent hide-close-button class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle class="text-xl">ETA Reminder</DialogTitle>
        <DialogDescription> Provider ETA has been reached. Please follow up on this case. </DialogDescription>
      </DialogHeader>

      <div v-if="props.case" class="py-4 space-y-3">
        <div class="grid grid-cols-2 gap-3">
          <div class="space-y-1">
            <p class="text-sm font-medium text-muted-foreground">Case Reg Num</p>
            <p
              class="text-sm font-semibold text-blue-600 cursor-pointer hover:underline"
              @click="handleViewCaseWithoutAccept"
            >
              {{ props.case.regNum }}
            </p>
          </div>

          <div class="space-y-1">
            <p class="text-sm font-medium text-muted-foreground">Client Reg Num</p>
            <p class="text-sm font-semibold">{{ props.case.clientRegNum }}</p>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div class="space-y-1">
            <p class="text-sm font-medium text-muted-foreground">Status</p>
            <p class="text-sm" :class="statusDisplayData.text">{{ caseStatus }}</p>
          </div>

          <div v-if="props.case.userData?.creator?.displayName" class="space-y-1">
            <p class="text-sm font-medium text-muted-foreground">Creator</p>
            <p class="text-sm font-semibold">{{ props.case.userData?.creator?.displayName }}</p>
          </div>
        </div>

        <div class="space-y-1">
          <p class="text-sm font-medium text-muted-foreground">Customer</p>
          <p class="text-sm">{{ customerName }}</p>
        </div>

        <div class="space-y-1">
          <p class="text-sm font-medium text-muted-foreground">Vehicle</p>
          <p class="text-sm">{{ vehicleInfo }}</p>
        </div>

        <div class="space-y-1">
          <p class="text-sm font-medium text-muted-foreground">Provider</p>
          <p class="text-sm">{{ providerName }}</p>
        </div>

        <div class="space-y-1">
          <p class="text-sm font-medium text-muted-foreground">Location</p>
          <p class="text-sm">{{ location }}</p>
        </div>

        <div
          class="p-3 space-y-1 bg-yellow-50 rounded-md border border-yellow-200 dark:bg-yellow-950/20 dark:border-yellow-900"
        >
          <p class="text-sm font-medium text-yellow-800 dark:text-yellow-200">Estimated Arrival</p>
          <p class="text-lg font-bold text-red-500 dark:text-yellow-100">{{ etaDisplay }}</p>
        </div>
      </div>

      <DialogFooter class="gap-2">
        <Button variant="outline" @click="handleDismiss">Dismiss</Button>
        <Button variant="default" @click="handleViewCase">View Case</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
